/** Tone classes for the sorted column of a data list (see `_data-list.scss`). */
export type ListTone =
  | ''
  | 'tone-pos'
  | 'tone-neg'
  | 'tone-mid'
  | 'tone-gold'
  | 'tone-accent';

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && !isNaN(value);

export function sentimentTone(value: number | null | undefined): ListTone {
  if (!isNumber(value)) return '';
  return value < 0.4 ? 'tone-neg' : value < 0.55 ? 'tone-mid' : 'tone-gold';
}

export function signTone(
  value: number | null | undefined,
  positiveIsGood = true,
): ListTone {
  if (!isNumber(value) || value === 0) return '';
  return value > 0 === positiveIsGood ? 'tone-pos' : 'tone-neg';
}

export function volumeRatioTone(value: number | null | undefined): ListTone {
  if (!isNumber(value)) return '';
  return value > 1 ? 'tone-pos' : value < 0.6 ? 'tone-neg' : '';
}

// The score colour pipes' 2/4/6/8 bands, low risk first.
const SCORE_BAND_TONES: ListTone[] = [
  'tone-pos',
  'tone-gold',
  'tone-mid',
  'tone-mid',
  'tone-neg',
];

/** A 0 score means "no data" for the score pipes, so it stays untoned. */
export function scoreTone(
  value: number | null | undefined,
  polarity: 'pos' | 'neg',
): ListTone {
  if (!isNumber(value) || value === 0) return '';
  const band = [2, 4, 6, 8].filter((edge) => value >= edge).length;
  return SCORE_BAND_TONES[polarity === 'pos' ? 4 - band : band];
}

/** What a list cell holds; it decides the cell's tone when its column is sorted. */
export type ToneKind =
  | 'sentiment'
  | 'sentiment-delta'
  | 'count'
  | 'ratio'
  | 'score'
  | 'score-delta'
  | 'price-delta';

/** Other kinds stay untoned, and so do scores without a polarity. */
export function valueTone(
  kind: string,
  value: number | null | undefined,
  polarity?: 'pos' | 'neg',
): ListTone {
  switch (kind) {
    case 'sentiment':
      return sentimentTone(value);
    case 'sentiment-delta':
    case 'price-delta':
      return signTone(Math.round(value * 100));
    case 'count':
      return 'tone-accent';
    case 'ratio':
      return volumeRatioTone(value);
    case 'score':
      return polarity ? scoreTone(value, polarity) : '';
    case 'score-delta':
      return polarity
        ? signTone(Math.round(value * 10) / 10, polarity === 'pos')
        : '';
    default:
      return '';
  }
}
